import React, { useState, useEffect, useRef, useCallback } from 'react';
import FontPicker from '../FontPicker';
import Effect3DControls from '../Effect3DControls';

function PropertyPanel({ canvasManager, activeObject, imageFilters, bgRemoval, onClose }) {
  const [fill, setFill] = useState('#000000');
  const [stroke, setStroke] = useState('');
  const [strokeWidth, setStrokeWidth] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const [fontSize, setFontSize] = useState(24);
  const [fontFamily, setFontFamily] = useState('Inter');
  const [fontWeight, setFontWeight] = useState('normal');
  const [fontStyle, setFontStyle] = useState('normal');
  const [textAlign, setTextAlign] = useState('left');
  const [textContent, setTextContent] = useState('');
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(0);
  const [bgRemoving, setBgRemoving] = useState(false);
  const [bgProgress, setBgProgress] = useState('');

  // Accordion: only one section open at a time
  const [openSection, setOpenSection] = useState('fill');

  // Drag state
  const [pos, setPos] = useState({ x: 0, y: 100 });
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const panelRef = useRef(null);

  const isText = activeObject ? ['i-text', 'text', 'textbox'].includes(activeObject.type) : false;
  const isImage = activeObject ? activeObject.type === 'image' : false;

  // Auto-open most relevant section when object type changes
  useEffect(() => {
    if (!activeObject) return;
    if (isText) setOpenSection('text');
    else if (isImage) setOpenSection('filters');
    else setOpenSection('fill');
  }, [activeObject?.type, isText, isImage]);

  useEffect(() => {
    if (!activeObject) return;
    setFill(activeObject.fill || '#000000');
    setStroke(activeObject.stroke || '');
    setStrokeWidth(activeObject.strokeWidth || 0);
    setOpacity(activeObject.opacity ?? 1);
    if (isText) {
      setFontSize(activeObject.fontSize || 24);
      setFontFamily(activeObject.fontFamily || 'Inter');
      setFontWeight(activeObject.fontWeight || 'normal');
      setFontStyle(activeObject.fontStyle || 'normal');
      setTextAlign(activeObject.textAlign || 'left');
      setTextContent(activeObject.text || '');
    }
  }, [activeObject, isText]);

  const updateTextContent = (value) => {
    if (!activeObject || !isText) return;
    setTextContent(value);
    activeObject.set('text', value);
    if (activeObject._clearCache) activeObject._clearCache();
    activeObject.dirty = true;
    if (activeObject.initDimensions) activeObject.initDimensions();
    activeObject.setCoords();
    canvasManager?.canvas?.renderAll();
  };

  // Drag handlers
  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('input, select, button')) return;
    setDragging(true);
    const rect = panelRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    e.preventDefault();
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e) => {
      setPos({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y });
    };
    const handleUp = () => setDragging(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [dragging]);

  if (!activeObject) return null;

  const update = (prop, value) => {
    activeObject.set(prop, value);
    // For text: ensure stroke renders behind fill with round joins
    if ((prop === 'stroke' || prop === 'strokeWidth') && isText) {
      if (activeObject.stroke && activeObject.strokeWidth > 0) {
        activeObject.set('paintFirst', 'stroke');
        activeObject.set('strokeLineJoin', 'round');
        activeObject.set('strokeLineCap', 'round');
      }
    }
    // For groups/SVGs: also apply fill/stroke to all child objects
    if ((prop === 'fill' || prop === 'stroke') && activeObject.type === 'group' && activeObject._objects) {
      activeObject._objects.forEach((child) => {
        if (child.fill && child.fill !== 'none' && child.fill !== 'transparent' && prop === 'fill') {
          child.set('fill', value);
        }
        if (prop === 'stroke') {
          child.set('stroke', value);
        }
      });
    }
    canvasManager.canvas.renderAll();
  };

  const handleRemoveBackground = async () => {
    if (!activeObject || activeObject.type !== 'image' || bgRemoving) return;
    setBgRemoving(true);
    setBgProgress({ text: 'KI-Modell wird geladen...', percent: 0, phase: 'download' });
    try {
      await bgRemoval.removeBackground(activeObject, (key, current, total) => {
        if (key === 'downloading') {
          setBgProgress({ text: 'KI-Modell laden', percent: Math.round((current / total) * 100), phase: 'download' });
        } else if (key === 'computing:0') {
          setBgProgress({ text: 'Bild wird analysiert...', percent: 100, phase: 'process' });
        } else {
          setBgProgress({ text: 'Hintergrund wird entfernt...', percent: 100, phase: 'process' });
        }
      });
      setBgProgress(null);
    } catch (err) {
      console.error('BG removal failed:', err);
      setBgProgress(null);
      alert('Hintergrund konnte nicht entfernt werden: ' + (err?.message || 'Unbekannter Fehler'));
    } finally {
      setBgRemoving(false);
    }
  };

  const toggle = (key) => setOpenSection(prev => prev === key ? null : key);

  const objectLabel = isText ? 'Text' : isImage ? 'Bild' : activeObject.type === 'rect' ? 'Rechteck' : activeObject.type === 'circle' ? 'Kreis' : 'Objekt';

  const style = pos.x === 0 && pos.y === 100
    ? {}
    : { position: 'fixed', left: pos.x, top: pos.y, right: 'auto' };

  // Accordion bar component
  const AccordionBar = ({ id, label, icon }) => {
    const isOpen = openSection === id;
    return (
      <button
        onClick={() => toggle(id)}
        className={`w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors ${
          isOpen
            ? 'bg-blue-50 text-blue-700 border-l-[3px] border-blue-500'
            : 'bg-white text-gray-600 hover:bg-gray-50 border-l-[3px] border-transparent'
        }`}
      >
        <span className="w-4 h-4 flex items-center justify-center shrink-0">{icon}</span>
        <span className="text-xs font-semibold flex-1">{label}</span>
        <svg
          className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
        >
          <path d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
    );
  };

  return (
    <div
      ref={panelRef}
      className="bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col"
      style={{ width: 280, maxHeight: 'calc(100vh - 80px)', overflow: 'hidden', ...style }}
    >
      {/* Draggable Header */}
      <div
        onMouseDown={handleMouseDown}
        className={`flex items-center justify-between px-3 py-2 border-b border-gray-200 select-none shrink-0 ${dragging ? 'cursor-grabbing' : 'cursor-grab'} bg-gray-50`}
      >
        <div className="flex items-center gap-2">
          <svg className="w-3.5 h-3.5 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="5" cy="5" r="2"/><circle cx="12" cy="5" r="2"/><circle cx="19" cy="5" r="2"/>
            <circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/>
            <circle cx="5" cy="19" r="2"/><circle cx="12" cy="19" r="2"/><circle cx="19" cy="19" r="2"/>
          </svg>
          <span className="text-xs font-semibold text-gray-700">Eigenschaften</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{objectLabel}</span>
          {onClose && (
            <button
              onClick={onClose}
              className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          )}
        </div>
      </div>

      {/* Accordion sections - vertical stacked */}
      <div className="overflow-y-auto flex-1">

        {/* === FUELLFARBE === */}
        {!isImage && (
          <>
            <AccordionBar id="fill" label="Füllfarbe" icon={
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M19.228 18.228l1.768-1.768a2 2 0 000-2.828L12.318 4.95l-8.486 8.486a2 2 0 000 2.828l1.768 1.768a2 2 0 002.828 0L12.318 14.1l3.89 3.89a2 2 0 002.828 0z"/><path d="M20 20.5c0 .828-.895 1.5-2 1.5s-2-.672-2-1.5S17.5 18 18 17s2 2.5 2 3.5z"/></svg>
            } />
            {openSection === 'fill' && (
              <div className="px-3 py-3 border-b border-gray-100 space-y-2">
                <input
                  type="color"
                  value={typeof fill === 'string' ? fill : '#000000'}
                  onChange={(e) => { setFill(e.target.value); update('fill', e.target.value); }}
                  className="w-full h-10 cursor-pointer rounded-lg border border-gray-200"
                />
                <div className="flex gap-1 flex-wrap">
                  {['#000000','#ffffff','#ef4444','#f97316','#eab308','#22c55e','#3b82f6','#8b5cf6','#ec4899','#6b7280'].map((c) => (
                    <button
                      key={c}
                      onClick={() => { setFill(c); update('fill', c); }}
                      className={`w-6 h-6 rounded-md border-2 transition-transform hover:scale-110 ${fill === c ? 'border-blue-500 scale-110' : 'border-gray-200'}`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* === RAHMEN === */}
        <AccordionBar id="stroke" label="Rahmen" icon={
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>
        } />
        {openSection === 'stroke' && (
          <div className="px-3 py-3 border-b border-gray-100 space-y-2">
            <div className="flex gap-2">
              <div className="flex-1 min-w-0">
                <label className="text-[10px] text-gray-500">Farbe</label>
                <input
                  type="color"
                  value={stroke || '#000000'}
                  onChange={(e) => { setStroke(e.target.value); update('stroke', e.target.value); }}
                  className="w-full h-10 cursor-pointer rounded-lg border border-gray-200"
                />
              </div>
              <div className="w-16 shrink-0">
                <label className="text-[10px] text-gray-500">Stärke</label>
                <input
                  type="number" value={strokeWidth}
                  onChange={(e) => { const v = Math.max(0, Math.min(200, Number(e.target.value) || 0)); setStrokeWidth(v); update('strokeWidth', v); }}
                  className="selenny-input text-xs py-2 w-full" min="0" max="200"
                />
              </div>
            </div>
            <div className="flex gap-1">
              {[0, 2, 5, 10, 20, 40].map((w) => (
                <button
                  key={w}
                  onClick={() => { setStrokeWidth(w); update('strokeWidth', w); }}
                  className={`flex-1 py-1.5 rounded-lg text-[10px] font-medium transition-all ${strokeWidth === w ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  {w === 0 ? 'Ohne' : `${w}px`}
                </button>
              ))}
            </div>
            <input type="range" min="0" max="80" step="1" value={Math.min(strokeWidth, 80)}
              onChange={(e) => { const v = Number(e.target.value); setStrokeWidth(v); update('strokeWidth', v); }}
              className="w-full" />
          </div>
        )}

        {/* === TRANSPARENZ === */}
        <AccordionBar id="opacity" label={`Transparenz ${Math.round(opacity * 100)}%`} icon={
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" opacity="0.5"/></svg>
        } />
        {openSection === 'opacity' && (
          <div className="px-3 py-3 border-b border-gray-100 space-y-3">
            <input
              type="range" min="0" max="1" step="0.05" value={opacity}
              onChange={(e) => { const v = Number(e.target.value); setOpacity(v); update('opacity', v); }}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
            />
            <div className="flex gap-1">
              {[1, 0.75, 0.5, 0.25, 0.1].map((v) => (
                <button
                  key={v}
                  onClick={() => { setOpacity(v); update('opacity', v); }}
                  className={`flex-1 py-1.5 rounded-lg text-[10px] font-medium transition-all ${Math.abs(opacity - v) < 0.02 ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  {Math.round(v * 100)}%
                </button>
              ))}
            </div>
          </div>
        )}

        {/* === SCHRIFT (nur bei Text) === */}
        {isText && (
          <>
            <AccordionBar id="text" label="Schrift" icon={
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 7V4h16v3M9 20h6M12 4v16"/></svg>
            } />
            {openSection === 'text' && (
              <div className="px-3 py-3 border-b border-gray-100 space-y-3">
                <div>
                  <label className="text-[10px] text-gray-500 mb-1 block">Text</label>
                  <textarea
                    value={textContent}
                    onChange={(e) => updateTextContent(e.target.value)}
                    rows={2}
                    spellCheck={true}
                    lang="de"
                    placeholder="Text eingeben..."
                    className="selenny-input text-sm py-2 w-full resize-y"
                    style={{ minHeight: '60px' }}
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500">Schriftart</label>
                  <FontPicker value={fontFamily} onChange={(f) => { setFontFamily(f); update('fontFamily', f); }} />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 min-w-0">
                    <label className="text-[10px] text-gray-500">Größe</label>
                    <input
                      type="number" value={fontSize}
                      onChange={(e) => { const v = Number(e.target.value); setFontSize(v); update('fontSize', v); }}
                      className="selenny-input text-xs py-2 w-full" min="8" max="500"
                    />
                  </div>
                  <div className="flex gap-1 items-end pb-0.5 shrink-0">
                    <button
                      onClick={() => { const v = fontWeight === 'bold' ? 'normal' : 'bold'; setFontWeight(v); update('fontWeight', v); }}
                      className={`w-9 h-9 rounded-lg text-sm font-bold flex items-center justify-center transition-all ${fontWeight === 'bold' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >B</button>
                    <button
                      onClick={() => { const v = fontStyle === 'italic' ? 'normal' : 'italic'; setFontStyle(v); update('fontStyle', v); }}
                      className={`w-9 h-9 rounded-lg text-sm italic flex items-center justify-center transition-all ${fontStyle === 'italic' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >I</button>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-gray-500">Ausrichtung</label>
                  <div className="flex gap-1">
                    {[
                      { key: 'left', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 10H3M21 6H3M21 14H3M17 18H3"/></svg> },
                      { key: 'center', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 10H6M21 6H3M21 14H3M18 18H6"/></svg> },
                      { key: 'right', icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10H7M21 6H3M21 14H3M21 18H7"/></svg> },
                    ].map((a) => (
                      <button
                        key={a.key}
                        onClick={() => { setTextAlign(a.key); update('textAlign', a.key); }}
                        className={`flex-1 h-9 rounded-lg flex items-center justify-center transition-all ${textAlign === a.key ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                      >
                        {a.icon}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-gray-500">Schnellauswahl</label>
                  <div className="flex gap-1 flex-wrap">
                    {[12, 16, 24, 36, 48, 72, 96].map((s) => (
                      <button
                        key={s}
                        onClick={() => { setFontSize(s); update('fontSize', s); }}
                        className={`px-2 py-1 rounded-lg text-[10px] font-medium transition-all ${fontSize === s ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                      >
                        {s}px
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* === BILDFILTER (nur bei Bild) === */}
        {isImage && imageFilters && (
          <>
            <AccordionBar id="filters" label="Bildfilter" icon={
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><path d="M12 3a9 9 0 010 18"/><path d="M3 12h18"/></svg>
            } />
            {openSection === 'filters' && (
              <div className="px-3 py-3 border-b border-gray-100 space-y-3">
                <div>
                  <label className="text-[10px] text-gray-500">Helligkeit: {brightness}</label>
                  <input
                    type="range" min="-1" max="1" step="0.05" value={brightness}
                    onChange={(e) => { const v = Number(e.target.value); setBrightness(v); imageFilters.applyBrightness(activeObject, v); }}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500">Kontrast: {contrast}</label>
                  <input
                    type="range" min="-1" max="1" step="0.05" value={contrast}
                    onChange={(e) => { const v = Number(e.target.value); setContrast(v); imageFilters.applyContrast(activeObject, v); }}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                  />
                </div>
                <div className="flex gap-1">
                  <button onClick={() => imageFilters.applyGrayscale(activeObject)} className="text-[11px] py-2 px-3 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all flex-1">Grau</button>
                  <button onClick={() => imageFilters.applySepia(activeObject)} className="text-[11px] py-2 px-3 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all flex-1">Sepia</button>
                  <button onClick={() => imageFilters.applyInvert(activeObject)} className="text-[11px] py-2 px-3 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all flex-1">Invertieren</button>
                </div>
                <button onClick={() => imageFilters.removeFilters(activeObject)} className="w-full text-[11px] py-2 px-3 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-all">Alle Filter zurücksetzen</button>
              </div>
            )}
          </>
        )}

        {/* === KI HINTERGRUND ENTFERNEN (immer sichtbar bei Bild) === */}
        {isImage && bgRemoval && (
          <div className="px-3 py-3 border-b border-gray-100 dark:border-gray-700">
            <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">KI-Freisteller</p>
            {bgRemoving && bgProgress ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 animate-spin text-purple-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
                  <span className="text-[11px] font-medium text-purple-600 dark:text-purple-300">{bgProgress.text}</span>
                </div>
                <div className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{
                    width: bgProgress.phase === 'process' ? '100%' : bgProgress.percent + '%',
                    background: bgProgress.phase === 'process' ? 'linear-gradient(90deg, #8b5cf6, #a78bfa)' : 'linear-gradient(90deg, #6d28d9, #8b5cf6)',
                    animation: bgProgress.phase === 'process' ? 'pulse 1.5s ease-in-out infinite' : 'none',
                  }} />
                </div>
                <p className="text-[9px] text-gray-400 dark:text-gray-500 text-center">
                  {bgProgress.phase === 'download' ? 'Beim ersten Mal wird das KI-Modell heruntergeladen (~40 MB)' : 'Das kann einige Sekunden dauern...'}
                </p>
              </div>
            ) : (
              <>
                <button
                  onClick={handleRemoveBackground}
                  disabled={bgRemoving}
                  className="w-full text-[11px] py-2.5 px-3 rounded-lg transition-all flex items-center justify-center gap-2 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/50 font-medium"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9.75 3a3 3 0 0 0-3 3v.75H5.25a.75.75 0 0 0 0 1.5h.39l.6 9.6A2.25 2.25 0 0 0 8.49 20.1h7.02a2.25 2.25 0 0 0 2.25-2.25l.6-9.6h.39a.75.75 0 0 0 0-1.5h-1.5V6a3 3 0 0 0-3-3h-4.5z"/><path d="M4 12h16M12 4v16" strokeDasharray="2 2"/></svg>
                  Hintergrund entfernen (KI)
                </button>
                <p className="text-[9px] text-gray-400 dark:text-gray-500 mt-1.5 text-center">Entfernt den Hintergrund automatisch per KI</p>
              </>
            )}
          </div>
        )}

        {/* 3D-Effekt-Einstellungen */}
        <Effect3DControls activeObject={activeObject} canvasManager={canvasManager} />

        {/* === AKTIONEN === */}
        <AccordionBar id="actions" label="Aktionen" icon={
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
        } />
        {openSection === 'actions' && (
          <div className="px-3 py-3 border-b border-gray-100 space-y-2">
            {/* Lock / Unlock */}
            <button
              onClick={() => canvasManager?.toggleLock?.()}
              className={`w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-[11px] font-medium transition-all ${
                activeObject?.isLocked
                  ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title="Ctrl+L"
            >
              {activeObject?.isLocked ? (
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
              ) : (
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 019.9-1"/></svg>
              )}
              {activeObject?.isLocked ? 'Entsperren' : 'Sperren'}
            </button>

            {/* Group (only for multi-selection) */}
            {activeObject?.type === 'activeSelection' && (
              <button
                onClick={() => canvasManager?.groupSelected?.()}
                className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-[11px] font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 transition-all"
                title="Ctrl+G"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="8" height="8" rx="1"/><rect x="14" y="14" width="8" height="8" rx="1"/><path d="M6 14v-4h4M18 10v4h-4"/></svg>
                Gruppieren
              </button>
            )}

            {/* Ungroup (only for groups) */}
            {activeObject?.type === 'group' && !activeObject?._curvedText && (
              <button
                onClick={() => canvasManager?.ungroupSelected?.()}
                className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-[11px] font-medium bg-orange-50 text-orange-700 hover:bg-orange-100 transition-all"
                title="Ctrl+Shift+G"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="8" height="8" rx="1"/><rect x="14" y="14" width="8" height="8" rx="1"/><path d="M14 6h4v4M10 18H6v-4"/></svg>
                Gruppierung aufheben
              </button>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

export default PropertyPanel;
